import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PopoverBody, UncontrolledPopover } from 'reactstrap';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight, Edit2, ExternalLink, Users, X } from 'react-feather';
import { getStudents, studentCourses, courseLevel, completedLessonByLevel } from '../../../Api/apiUtils';
import ClickToCall from '../../../Telephony/ClickToCall';
import {
  Badge,
  Button,
  Card,
  DescriptionList,
  Disclosure,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  Select,
  Table,
  Toolbar,
} from '../../../ui';
import type { BadgeTone } from '../../../ui';
import LearnerTaskList from './LearnerTaskList';
import { LocalTimeDisplay } from './TaskActionModal';
import {
  Drawer,
  HEALTH_TONE,
  HealthBadge,
  IconButton,
  Segmented,
} from './ui-pending';
import './MyLearnersPage.scss';
import {
  fetchTasksMe,
  isTasksLeaderRole,
  TasksMe,
  currentActor,
  learnerCountry,
  customerLocalTime,
  HealthBand,
  HealthBreakdown,
  HEALTH_COMPONENT_LABELS,
  PortfolioHealth,
  RM_TASKS_ENABLED,
  tasksApi,
} from '../../../Tasks/tasksApi';

const BAND_LABEL: Record<HealthBand, string> = {
  HEALTHY: 'Healthy',
  WATCHLIST: 'Watchlist',
  AT_RISK: 'At Risk',
  CRITICAL: 'Critical',
};

export type Learner = PortfolioHealth['learners'][number];

/** Fallback while the server list loads (or on an older backend). */
const DEFAULT_ALLOWED_TAGS = ['VIP', 'IRATE USER', 'DND'];
const TAG_TONE = (tag: string): BadgeTone => {
  if (tag === 'VIP') return 'warning';
  if (tag === 'IRATE USER') return 'danger';
  if (tag === 'DND') return 'neutral';
  return 'primary';
};

/** Score + band chip, the same in the table and the drawer. */
const HealthChip: React.FC<{ band: HealthBand; score: number }> = ({ band, score }) => (
  <HealthBadge
    band={band}
    label={
      <>
        <span className="ml-health__score">{score}</span>
        {BAND_LABEL[band]}
      </>
    }
  />
);

/** Component scores behind a health total. */
const BreakdownTable: React.FC<{ breakdown: HealthBreakdown | null }> = ({ breakdown }) => (
  <table className="ml-breakdown">
    <tbody>
      {HEALTH_COMPONENT_LABELS.map(({ key, label }) => (
        <tr key={key}>
          <td className="ds-text-muted">{label}</td>
          <td className="ml-breakdown__value">{breakdown && breakdown[key] != null ? breakdown[key] : '—'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * Health capsule + score bar; hovering (or focusing) it opens a popover with
 * every component behind the total. Popover-based so it can't be clipped by
 * the table's horizontal scroll.
 */
const HealthCell: React.FC<{ learner: Learner }> = ({ learner: l }) => {
  if (!l.band || l.score == null) {
    return <em className="rm-caption">Not scored yet</em>;
  }
  const tone = HEALTH_TONE[l.band] ?? 'neutral';
  const targetId = `hs-${String(l.studentExternalId).replace(/[^A-Za-z0-9_-]/g, '')}`;
  const breakdown = l.breakdown ?? null;
  const hasBreakdown =
    !!breakdown && HEALTH_COMPONENT_LABELS.some(({ key }) => breakdown[key] != null);
  return (
    <>
      <span id={targetId} className="ml-health">
        <HealthChip band={l.band} score={l.score} />
        <span className="ml-health__track">
          {/* Data-driven width: the score as a percentage. */}
          <span
            className={`ml-health__fill ml-health__fill--${tone}`}
            style={{ width: `${Math.max(4, Math.min(100, l.score))}%` }}
          />
        </span>
      </span>
      <UncontrolledPopover target={targetId} trigger="hover focus" placement="right" fade={false}>
        <PopoverBody className="ml-pop">
          <div className="rm-row rm-row--between">
            <span className="ds-strong">Health score</span>
            <span className={`ml-pop__total ml-tone--${tone}`}>{l.score}/100</span>
          </div>
          {hasBreakdown ? (
            <BreakdownTable breakdown={breakdown} />
          ) : (
            <div className="rm-caption">Component scores not reported for this period.</div>
          )}
          {l.period && <div className="rm-caption">As of {l.period}</div>}
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );
};

/** RM-editable tags shown against the student; whole set saves per change. */
const TagChips: React.FC<{
  learner: Learner;
  allowedTags: string[];
  onSaved: (studentExternalId: string, tags: string[]) => void;
}> = ({ learner, allowedTags, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  // Tolerate payloads from a backend that predates tags (e.g. mid-deploy).
  const tags = learner.tags ?? [];

  const save = async (tags: string[]): Promise<void> => {
    setBusy(true);
    try {
      await tasksApi(`/v1/agent-student-maps/${learner.studentExternalId}/tags`, {
        method: 'POST',
        body: JSON.stringify({ tags }),
      });
      onSaved(learner.studentExternalId, tags);
    } catch (err: any) {
      toast.error(err.message || 'Could not save tags');
    } finally {
      setBusy(false);
    }
  };

  const add = (tag: string): void => {
    if (tags.includes(tag)) return;
    save([...tags, tag]);
  };

  return (
    <div className="ml-tags">
      {tags.map((tag) => (
        <Badge key={tag} tone={TAG_TONE(tag)}>
          {tag}
          {editing && (
            <button
              type="button"
              className="ml-tag-remove"
              title={`Remove ${tag}`}
              aria-label={`Remove ${tag}`}
              onClick={() => save(tags.filter((x) => x !== tag))}
            >
              <X size={10} aria-hidden />
            </button>
          )}
        </Badge>
      ))}
      <button
        type="button"
        className="ml-tag-btn ml-tag-btn--edit"
        title={editing ? 'Done' : 'Edit tags'}
        aria-label={editing ? 'Done editing tags' : 'Edit tags'}
        disabled={busy}
        onClick={() => setEditing((e) => !e)}
      >
        {editing ? 'done' : tags.length ? <Edit2 size={10} aria-hidden /> : '+ tag'}
      </button>
      {editing &&
        allowedTags
          .filter((q) => !tags.includes(q))
          .map((q) => (
            <button
              key={q}
              type="button"
              className={`ml-tag-btn ml-tone--${TAG_TONE(q)}`}
              disabled={busy}
              onClick={() => add(q)}
            >
              + {q}
            </button>
          ))}
    </div>
  );
};

/**
 * Slide-in student profile pane. Opens from View instead of navigating away —
 * a consolidated RM view (identity, contact, local time, RM, tags, health
 * breakdown, this student's tasks) plus a button to the full CMS profile.
 */
export const StudentDetailDrawer: React.FC<{
  learner: Learner | null;
  allowedTags: string[];
  onClose: () => void;
  onTagsSaved: (studentExternalId: string, tags: string[]) => void;
  onOpenFull: (l: Learner) => void;
  resolving: boolean;
}> = ({ learner: l, allowedTags, onClose, onTagsSaved, onOpenFull, resolving }) => {
  const [rmName, setRmName] = useState<string | null>(null);
  // The list owns its own fetch now; the drawer only needs the number for
  // its heading.
  const [taskCount, setTaskCount] = useState<number | null>(null);
  const [tasksOpen, setTasksOpen] = useState(true);
  // Course data comes from the CMS student APIs (not the tasks service).
  // undefined = loading, null = no course / couldn't resolve.
  const [courses, setCourses] = useState<any[] | null | undefined>(undefined);
  const [selCourseId, setSelCourseId] = useState<string | null>(null);
  const [lessons, setLessons] = useState<{ completed: number; total: number } | null>(null);
  // Enrollment date = the course-level row's created_at (from courseLevel).
  const [enrolledAt, setEnrolledAt] = useState<string | null>(null);

  useEffect(() => {
    if (!l) return;
    setRmName(null);
    setTaskCount(null);
    tasksApi<{ rm: { name: string } }>(`/v1/students/${l.studentExternalId}/rm`)
      .then((r) => setRmName(r?.rm?.name ?? null))
      .catch(() => setRmName(null));
  }, [l]);

  // Course list from the CMS. Best-effort: resolves the CMS studentId (from
  // mapping metadata, else by phone/name) then reads the same student-courses
  // API the profile page uses. A student may have several courses.
  useEffect(() => {
    if (!l) return;
    let cancelled = false;
    setCourses(undefined);
    setSelCourseId(null);
    (async () => {
      try {
        let studentId = l.metadata?.studentId as string | undefined;
        if (!studentId) {
          // userId first: it is what we match on and the only field that
          // cannot have drifted since onboarding.
          let rows: any[] = await getStudents(0, 20, { userId: l.studentExternalId });
          let hit = rows.find((r) => String(r.userId) === l.studentExternalId);
          if (!hit) {
            const last10 = (l.phoneE164 ?? '').replace(/\D/g, '').slice(-10);
            rows = last10 ? await getStudents(0, 20, { mobileNumber: last10 }) : [];
            hit = rows.find((r) => String(r.userId) === l.studentExternalId);
          }
          if (!hit) {
            rows = await getStudents(0, 20, { name: l.name });
            hit = rows.find((r) => String(r.userId) === l.studentExternalId);
          }
          studentId = hit?.studentId ? String(hit.studentId) : undefined;
        }
        if (!studentId) {
          if (!cancelled) setCourses(null);
          return;
        }
        const list = await studentCourses(studentId);
        const arr = Array.isArray(list) ? list : [];
        if (cancelled) return;
        setCourses(arr);
        const def = arr.find((x: any) => !x.isDemoCourse) || arr[0];
        if (def) setSelCourseId(String(def.courseId));
      } catch {
        if (!cancelled) setCourses(null);
      }
    })();
    return () => { cancelled = true; };
  }, [l]);

  const selectedCourse =
    courses && selCourseId ? courses.find((c) => String(c.courseId) === selCourseId) ?? null : null;

  // Lessons for the selected course (its own level → completed count).
  useEffect(() => {
    const c = selectedCourse;
    if (!c) {
      setLessons(null);
      return;
    }
    let cancelled = false;
    setLessons(null);
    setEnrolledAt(null);
    (async () => {
      if (!c.studentUserId || !c.courseId) return;
      try {
        const lvl = await courseLevel(c.studentUserId, c.courseId);
        const row = lvl?.status?.[0];
        const levelId = row?.level_id;
        if (!cancelled) setEnrolledAt(row?.created_at ?? null);
        const done = levelId ? await completedLessonByLevel(c.studentUserId, levelId) : null;
        if (!cancelled) {
          setLessons({
            completed: done?.completedLessonsByLevel ?? 0,
            total: done?.totalLessons ?? c.totalLessons ?? 0,
          });
        }
      } catch {
        if (!cancelled) setLessons({ completed: 0, total: c.totalLessons ?? 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [selCourseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtDate = (d: any): string | null => {
    if (!d) return null;
    const dt = new Date(d);
    return isNaN(dt.getTime())
      ? null
      : dt.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/,/g, '');
  };

  const country = l ? learnerCountry(l.countryTzone, l.phoneE164) : null;
  const local = l
    ? customerLocalTime({ context: { timezone: l.timezone ?? l.metadata?.timezone }, customerPhoneE164: l.phoneE164 })
    : null;
  const breakdown = l?.breakdown ?? null;
  const hasBreakdown =
    !!breakdown && HEALTH_COMPONENT_LABELS.some(({ key }) => breakdown[key] != null);

  if (!l) return null;

  const classStats: { label: string; value: number; tone: BadgeTone }[] = selectedCourse
    ? [
        { label: 'Purchased', value: selectedCourse.totalClasses ?? 0, tone: 'success' },
        { label: 'Completed', value: selectedCourse.completedClasses ?? 0, tone: 'success' },
        { label: 'Scheduled', value: selectedCourse.scheduledClasses ?? 0, tone: 'info' },
        { label: 'Unscheduled', value: selectedCourse.unscheduledClasses ?? 0, tone: 'info' },
        { label: 'Expired', value: selectedCourse.expiredClasses ?? 0, tone: 'neutral' },
        { label: 'Deducted', value: selectedCourse.deductedClasses ?? 0, tone: 'danger' },
      ]
    : [];

  const courseBlock =
    courses === undefined ? (
      <Loader label="Loading courses…" />
    ) : !courses || courses.length === 0 || !selectedCourse ? (
      <span className="ds-text-muted">Course details unavailable</span>
    ) : (
      <div className="ml-course">
        {courses.length > 1 ? (
          <Select
            size="sm"
            aria-label="Course"
            value={selCourseId ?? ''}
            onChange={(e) => setSelCourseId(e.target.value)}
            options={courses.map((c) => ({
              value: String(c.courseId),
              label: `${c.courseName}${c.isDemoCourse ? ' (Demo)' : ''}`,
            }))}
          />
        ) : (
          <div className="ds-strong">{selectedCourse.courseName || '—'}</div>
        )}
        {/* Whose course this is. Reads off selectedCourse, so it
            follows the dropdown rather than needing its own fetch.
            New tab on purpose: the RM is mid-ticket underneath. */}
        <div className="rm-caption">
          Teacher:{' '}
          {!selectedCourse.teacherName ? (
            <span className="ml-course__value">—</span>
          ) : selectedCourse.teacherCredentialId && selectedCourse.teacherId ? (
            <a
              href={`${process.env.PUBLIC_URL}/pages/teacher-profile/${selectedCourse.teacherCredentialId}/${selectedCourse.teacherId}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open this teacher's profile in a new tab"
              className="ml-link"
            >
              {selectedCourse.teacherName}
            </a>
          ) : (
            // Named but unlinkable — show who it is rather than a
            // link that goes nowhere.
            <span className="ml-course__value ds-strong">{selectedCourse.teacherName}</span>
          )}
        </div>

        <div className="ml-course__dates rm-caption">
          <span>
            Enrolled: <span className="ml-course__value">{fmtDate(enrolledAt) ?? '—'}</span>
          </span>
          <span>
            Expiry: <span className="ml-course__value">{fmtDate(selectedCourse.expiryDate) ?? '—'}</span>
          </span>
        </div>

        {/* Classes grid */}
        <div className="ml-classes">
          {classStats.map((s) => (
            <div key={s.label} className="ml-classes__tile">
              <div className={`ml-classes__value ml-tone--${s.tone}`}>{s.value}</div>
              <div className="ml-classes__label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Lessons */}
        <div className="rm-row rm-row--between">
          <span className="rm-caption">Lessons (Completed / Total)</span>
          <span className="ml-lessons ml-tone--info">
            {lessons ? `${lessons.completed} / ${lessons.total}` : `— / ${selectedCourse.totalLessons ?? 0}`}
          </span>
        </div>
      </div>
    );

  // Drawer portals into <body>: `position: fixed` resolves against the
  // nearest ancestor carrying a transform/filter rather than the viewport,
  // and the theme puts one on a page wrapper — which pushed the panel's own
  // header (name and close button) above the top of the screen.
  return (
    <Drawer
      open
      onClose={onClose}
      ariaLabel={`${l.name} details`}
      title={l.name}
      headerExtra={country && <span className={`fi fi-${country.iso} rm-flag`} title={country.country} />}
      footer={
        <Button
          variant="primary"
          block
          disabled={resolving}
          icon={resolving ? undefined : <ExternalLink size={14} aria-hidden />}
          onClick={() => onOpenFull(l)}
        >
          {resolving ? 'Opening…' : 'Open full profile'}
        </Button>
      }
    >
      <div className="ml-details">
        {/* The CMS userId, which is what this service keys learners on.
            Not the studentId — that is a different number living in
            metadata (221382 vs 214926 for the same learner), and calling
            this one "Student ID" sent people looking up the wrong one. */}
        <DescriptionList
          items={[
            { label: 'User ID', value: <code className="rm-mono">{l.studentExternalId}</code> },
            {
              label: 'Contact',
              value: (
                <>
                  {l.phoneE164 ? (
                    <ClickToCall
                      number={l.phoneE164}
                      name={l.name}
                      customerUserId={l.studentExternalId}
                      source="admin"
                      sourceRecordId={l.studentExternalId}
                      fallbackToDialer
                      inline
                    />
                  ) : (
                    <span className="ds-text-muted">No phone</span>
                  )}
                  {l.email && <div className="rm-caption">{l.email}</div>}
                </>
              ),
            },
            ...(local ? [{ label: 'Local time', value: <LocalTimeDisplay local={local} /> }] : []),
            { label: 'Relationship manager', value: rmName ?? <span className="ds-text-muted">—</span> },
            {
              label: 'Tags',
              value: <TagChips learner={l} allowedTags={allowedTags} onSaved={onTagsSaved} />,
            },
            { label: 'Course', value: courseBlock },
            {
              label: 'Health',
              value:
                l.band && l.score != null ? (
                  <div className="ds-stack">
                    <span>
                      <HealthChip band={l.band} score={l.score} />
                    </span>
                    {hasBreakdown && <BreakdownTable breakdown={breakdown} />}
                  </div>
                ) : (
                  <span className="ds-text-muted">Not scored yet</span>
                ),
            },
          ]}
        />
      </div>

      <Disclosure
        open={tasksOpen}
        onToggle={() => setTasksOpen((o) => !o)}
        summary={<span className="ds-strong">Tasks{taskCount === null ? '' : ` (${taskCount})`}</span>}
      >
        <LearnerTaskList studentExternalId={l.studentExternalId} onCount={setTaskCount} />
      </Disclosure>
    </Drawer>
  );
};

/**
 * Consumer Xperience → My Learners: the RM's assigned portfolio, from the
 * agent-student mapping + health scores. Classes / teacher / last class /
 * expiry / open issues columns wait on core-system APIs and render as "—"
 * until those exist.
 */
const MyLearnersPage: React.FC = () => {
  const me = useMemo(currentActor, []);
  const [portfolio, setPortfolio] = useState<PortfolioHealth | null>(null);
  const [tasksMe, setTasksMe] = useState<TasksMe | null>(null);
  const isLeader = isTasksLeaderRole(tasksMe?.role);
  const [agentFilter, setAgentFilter] = useState('');
  const [allowedTags, setAllowedTags] = useState<string[]>(DEFAULT_ALLOWED_TAGS);
  useEffect(() => {
    tasksApi<{ tags: string[] }>('/v1/agent-student-maps/tags/allowed')
      .then((r) => r.tags?.length && setAllowedTags(r.tags))
      .catch(() => undefined); // older backend — keep the fallback list
  }, []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bandFilter, setBandFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    if (!me.externalId) {
      setLoading(false);
      return;
    }
    try {
      const mine = await fetchTasksMe();
      setTasksMe(mine);
      // Team leaders and admins see every RM's students; agents see their own.
      const url = isTasksLeaderRole(mine?.role)
        ? '/v1/portfolio-health'
        : `/v1/agents/${me.externalId}/portfolio-health`;
      setPortfolio(await tasksApi<PortfolioHealth>(url));
    } catch {
      setPortfolio(null); // not provisioned as an RM yet → empty state
    } finally {
      setLoading(false);
    }
  }, [me.externalId]);

  useEffect(() => {
    load();
  }, [load]);

  const searchFiltered = useMemo(() => {
    const all = portfolio?.learners ?? [];
    const term = search.trim().toLowerCase();
    return all.filter(
      (l) =>
        (!term ||
          l.name.toLowerCase().includes(term) ||
          l.studentExternalId.includes(term) ||
          (l.phoneE164 ?? '').includes(term)) &&
        (!tagFilter || (l.tags ?? []).includes(tagFilter)) &&
        (!agentFilter || l.agent?.externalId === agentFilter) &&
        (!countryFilter || learnerCountry(l.countryTzone, l.phoneE164)?.iso === countryFilter),
    );
  }, [portfolio, search, tagFilter, agentFilter, countryFilter]);

  /** Agents present in the portfolio (leader view only), for the filter. */
  const agents = useMemo(() => {
    const seen = new Map<string, string>();
    (portfolio?.learners ?? []).forEach((l) => {
      if (l.agent) seen.set(l.agent.externalId, l.agent.name);
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [portfolio]);

  /** Countries actually present in the portfolio, for the filter options. */
  const countries = useMemo(() => {
    const seen = new Map<string, string>();
    (portfolio?.learners ?? []).forEach((l) => {
      const c = learnerCountry(l.countryTzone, l.phoneE164);
      if (c) seen.set(c.iso, c.country);
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [portfolio]);

  const learners = useMemo(
    () => searchFiltered.filter((l) => !bandFilter || l.band === bandFilter),
    [searchFiltered, bandFilter],
  );

  // Any filter or page-size change restarts from the first page.
  useEffect(() => {
    setPage(0);
  }, [search, bandFilter, tagFilter, agentFilter, countryFilter, pageSize]);

  const pageCount = Math.max(1, Math.ceil(learners.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pagedLearners = learners.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const [detail, setDetail] = useState<Learner | null>(null);

  /**
   * Opens the CMS's own /profile/:userId route in a new tab, which looks the
   * learner up and redirects to their profile.
   *
   * This used to resolve credentialId here — search the students API by
   * phone, then by name, then cache the ids back onto the mapping. Three
   * things were wrong with that. It searched the two fields most likely to
   * have drifted (learner 221382 is "surya" on one number here and
   * "sherebiya" on another in the CMS, so both searches missed). The
   * write-back sent name and phone but not email, and that endpoint writes
   * all three unconditionally, so every successful open quietly cleared the
   * learner's email. And it sent the viewer as the owner, so a leader
   * opening someone else's learner tried to move them onto themselves.
   *
   * None of it was needed: the CMS already resolves a userId, which is the
   * one identifier that cannot drift and the only one we are sure of.
   */
  const openProfile = (l: Learner): void => {
    window.open(
      `${process.env.PUBLIC_URL}/profile/${l.studentExternalId}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const exportCsv = (): void => {
    const header = 'studentUserId,name,phone,email,healthScore,band,scoredMonth';
    const rows = learners.map((l) =>
      [l.studentExternalId, `"${l.name}"`, l.phoneE164 ?? '', l.email ?? '', l.score ?? '', l.band ?? '', l.period ?? ''].join(','),
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'my-students.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        <PageHeader eyebrow="RM Tasks" title="My Students" />
        <EmptyState title="Not available yet" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }

  const bandOptions = (
    [
      { key: '', label: 'All' },
      ...Object.entries(BAND_LABEL).map(([band, label]) => ({ key: band, label })),
    ] as { key: string; label: string }[]
  ).map((chip) => {
    const count = chip.key
      ? searchFiltered.filter((l) => l.band === chip.key).length
      : searchFiltered.length;
    return { value: chip.key, label: `${chip.label} (${count})` };
  });

  return (
    <Page>
      <PageHeader
        eyebrow="RM Tasks"
        title="My Students"
        subtitle={isLeader ? 'Every student across your team, by RM.' : 'A complete view of your assigned student portfolio.'}
        actions={<Button onClick={exportCsv}>Export</Button>}
      />

      <Toolbar>
        <Field label="Search" className="ds-toolbar__grow">
          {(a) => (
            <SearchInput
              {...a}
              placeholder="Search student, id or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </Field>
        <Field label="Tag">
          {(a) => (
            <Select
              {...a}
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              options={[{ value: '', label: 'All tags' }, ...allowedTags.map((t) => ({ value: t, label: t }))]}
            />
          )}
        </Field>
        {isLeader && (
          <Field label="Agent">
            {(a) => (
              <Select
                {...a}
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                options={[
                  { value: '', label: 'All agents' },
                  ...agents.map(([externalId, name]) => ({ value: externalId, label: name })),
                ]}
              />
            )}
          </Field>
        )}
        <Field label="Country">
          {(a) => (
            <Select
              {...a}
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              options={[
                { value: '', label: 'All countries' },
                ...countries.map(([iso, name]) => ({ value: iso, label: name })),
              ]}
            />
          )}
        </Field>
      </Toolbar>

      <Card
        title="My student portfolio"
        subtitle={`${learners.length} student${learners.length === 1 ? '' : 's'}`}
      >
        <div className="ml-body">
          {/* Health chips — same pattern as the queue's urgency chips */}
          <Segmented
            ariaLabel="Filter by health"
            options={bandOptions}
            value={bandFilter}
            onChange={setBandFilter}
          />

          {loading ? (
            <Loader label="Loading learners…" />
          ) : learners.length === 0 ? (
            <EmptyState
              icon={<Users size={22} aria-hidden />}
              title="No students mapped to you yet."
              description="If you've filtered the list, clear the search and filters to see everyone."
            />
          ) : (
            <Table caption="My student portfolio">
              <thead>
                <tr>
                  <th>Student</th>
                  {isLeader && <th>Agent</th>}
                  <th>Health</th>
                  <th>Tags</th>
                  <th>Phone</th>
                  <th title="Customer's current local time">Local time</th>
                </tr>
              </thead>
              <tbody>
                {pagedLearners.map((l) => (
                  <tr key={l.studentExternalId}>
                    <td>
                      <div className="ml-student">
                        {(() => {
                          const c = learnerCountry(l.countryTzone, l.phoneE164);
                          return c ? <span className={`fi fi-${c.iso} rm-flag`} title={c.country} /> : null;
                        })()}
                        <button
                          type="button"
                          className="ml-link ml-link--button"
                          title="View profile details"
                          onClick={() => setDetail(l)}
                        >
                          {l.name}
                        </button>
                      </div>
                      <div className="rm-caption">UserId · {l.studentExternalId}</div>
                    </td>
                    {isLeader && (
                      <td>{l.agent ? l.agent.name : <span className="ds-text-muted">—</span>}</td>
                    )}
                    <td className="ml-col-health">
                      <HealthCell learner={l} />
                    </td>
                    <td className="ml-col-tags">
                      <TagChips
                        learner={l}
                        allowedTags={allowedTags}
                        onSaved={(sid, tags) =>
                          setPortfolio((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  learners: prev.learners.map((x) =>
                                    x.studentExternalId === sid ? { ...x, tags } : x,
                                  ),
                                }
                              : prev,
                          )
                        }
                      />
                    </td>
                    <td className="ml-col-phone">
                      {/* Fixed-width wrapper: the pinned button aligns in one
                          vertical line across rows, at a tight offset. */}
                      <div className="ml-call">
                        <ClickToCall
                          number={l.phoneE164}
                          name={l.name}
                          source="admin"
                          sourceRecordId={l.studentExternalId}
                          fallbackToDialer
                        />
                      </div>
                    </td>
                    <td className="ml-col-time">
                      {(() => {
                        const local = customerLocalTime({
                          // Explicit zone from student_details beats any
                          // metadata copy; countryTzone is a country NAME,
                          // never a zone, so it plays no part here.
                          context: { timezone: l.timezone ?? l.metadata?.timezone },
                          customerPhoneE164: l.phoneE164,
                        });
                        return local ? <LocalTimeDisplay local={local} /> : <span className="ds-text-muted">—</span>;
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {!loading && learners.length > 0 && (
            <div className="ml-pager">
              <span className="rm-caption ds-num">
                Showing {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, learners.length)} of{' '}
                {learners.length}
              </span>
              <div className="ml-pager__controls">
                <label className="ml-pager__size">
                  <span className="rm-caption">Rows per page</span>
                  <Select
                    size="sm"
                    value={String(pageSize)}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    options={[25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                  />
                </label>
                <IconButton
                  disabled={safePage === 0}
                  onClick={() => setPage(safePage - 1)}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} aria-hidden />
                </IconButton>
                <span className="ds-num">
                  {safePage + 1} / {pageCount}
                </span>
                <IconButton
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(safePage + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight size={16} aria-hidden />
                </IconButton>
              </div>
            </div>
          )}
        </div>
      </Card>

      <StudentDetailDrawer
        learner={detail}
        allowedTags={allowedTags}
        onClose={() => setDetail(null)}
        onTagsSaved={(sid, tags) => {
          setPortfolio((prev) =>
            prev
              ? { ...prev, learners: prev.learners.map((x) => (x.studentExternalId === sid ? { ...x, tags } : x)) }
              : prev,
          );
          setDetail((prev) => (prev && prev.studentExternalId === sid ? { ...prev, tags } : prev));
        }}
        onOpenFull={openProfile}
        resolving={false}
      />
    </Page>
  );
};

export default MyLearnersPage;
